/**
 * Usage and quota presentation (prompt.md §32, §33, §86).
 *
 * WHY: a plan quota is the one number that can pause a run, so it must read the
 * same everywhere it appears. Model spend and infrastructure spend are shown
 * separately, because BYOK removes the token cost but not the platform cost.
 */

import { Badge } from "../primitives/Feedback.tsx";
import { Card } from "../primitives/Surfaces.tsx";
import { MUTED_TEXT, stackStyle } from "../styles.ts";
import { formatTokens } from "../logic/format.ts";
import { quotaNotice, usageMeterView, type UsageMeterView } from "../logic/usage.ts";
import { toneColor, toneSurface } from "../logic/runState.ts";

export interface UsageMeterProps {
  readonly label: string;
  readonly used: number;
  readonly limit: number | null;
  readonly format?: (value: number) => string;
  readonly hint?: string;
}

/** A single quota bar. The bar is decorative; the text carries the meaning. */
export function UsageMeter({ label, used, limit, format, hint }: UsageMeterProps) {
  const view = usageMeterView({ used, limit, format });
  const notice = quotaNotice(view, label);
  return (
    <div style={stackStyle("var(--dayvise-space-xxs)")}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: "var(--dayvise-space-sm)" }}>
        <span style={{ fontSize: "var(--dayvise-font-size-sm)" }}>{label}</span>
        <span style={{ ...MUTED_TEXT, fontSize: "var(--dayvise-font-size-sm)" }}>
          {view.usedText} / {view.limitText}
        </span>
      </div>
      <div
        role="meter"
        aria-valuemin={0}
        aria-valuemax={view.limit ?? 0}
        aria-valuenow={view.used}
        aria-valuetext={view.ariaValueText}
        aria-label={label}
        style={{
          height: "8px",
          borderRadius: "var(--dayvise-radius-pill)",
          background: "var(--dayvise-color-surface)",
          border: "1px solid var(--dayvise-color-border)",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            width: `${Math.min(100, Math.round((view.ratio ?? 0) * 100))}%`,
            height: "100%",
            background: toneColor(view.tone),
          }}
        />
      </div>
      {notice ? (
        <span style={{ color: toneColor(view.tone), fontSize: "var(--dayvise-font-size-xs)" }}>{notice}</span>
      ) : hint ? (
        <span style={{ ...MUTED_TEXT, fontSize: "var(--dayvise-font-size-xs)" }}>{hint}</span>
      ) : null}
    </div>
  );
}

export interface UsagePanelProps {
  readonly aiTokens: { readonly used: number; readonly limit: number | null };
  readonly byokTokens: { readonly used: number; readonly limit: number | null };
  readonly containerMs: { readonly used: number; readonly limit: number | null };
  readonly storageBytes: { readonly used: number; readonly limit: number | null };
  readonly storageNotice?: string | null;
  readonly planLabel: string;
  readonly byokEnabled: boolean;
}

/**
 * The usage page body. Token meters and infrastructure meters are two separate
 * groups, which is the §32 rule made visible.
 */
export function UsagePanel({ aiTokens, byokTokens, containerMs, storageBytes, storageNotice, planLabel, byokEnabled }: UsagePanelProps) {
  const formatMs = (value: number): string => `${Math.round(value / 1000)}s`;
  const formatStorage = (value: number): string => `${(value / (1024 * 1024 * 1024)).toFixed(2)} GB`;

  return (
    <div style={stackStyle("var(--dayvise-space-md)")}>
      <Card title="Model usage" subtitle={`${planLabel} plan`}>
        <div style={stackStyle("var(--dayvise-space-sm)")}>
          <UsageMeter label="Dayvise-managed AI" used={aiTokens.used} limit={aiTokens.limit} format={formatTokens} hint="Tokens served by the managed provider" />
          {byokEnabled ? (
            <UsageMeter label="Your own provider" used={byokTokens.used} limit={null} format={formatTokens} hint="Billed by your provider, not by Dayvise" />
          ) : (
            <span style={{ ...MUTED_TEXT, fontSize: "var(--dayvise-font-size-sm)" }}>
              Bring your own provider key to route model calls to your own account.
            </span>
          )}
        </div>
      </Card>

      <Card title="Infrastructure usage" subtitle="Charged regardless of who provides the model">
        <div style={stackStyle("var(--dayvise-space-sm)")}>
          <UsageMeter label="Container time" used={containerMs.used} limit={containerMs.limit} format={formatMs} />
          <UsageMeter label="Storage" used={storageBytes.used} limit={storageBytes.limit} format={formatStorage} hint="Retained files, artifacts and runtime blobs" />
          {storageNotice ? <span style={{ ...MUTED_TEXT, fontSize: "var(--dayvise-font-size-sm)" }}>{storageNotice}</span> : null}
        </div>
      </Card>
    </div>
  );
}

export interface PlanSummaryProps {
  readonly plan: string;
  readonly isWorkContext: boolean;
  readonly entitlements: readonly { readonly label: string; readonly value: string }[];
}

/** The plan header shown on the usage page and in settings. */
export function PlanSummary({ plan, isWorkContext, entitlements }: PlanSummaryProps) {
  return (
    <Card title={`${plan} plan`} subtitle={isWorkContext ? "Managed by your organization" : "Personal workspace"}>
      <div style={stackStyle("var(--dayvise-space-xs)")}>
        <Badge tone={isWorkContext ? "accent" : "neutral"}>{isWorkContext ? "Workplace" : "Personal"}</Badge>
        <EntitlementList entitlements={entitlements} />
      </div>
    </Card>
  );
}

function EntitlementList({ entitlements }: { readonly entitlements: readonly { readonly label: string; readonly value: string }[] }) {
  return (
    <dl style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: "var(--dayvise-space-xxs) var(--dayvise-space-sm)", margin: 0 }}>
      {entitlements.map((entry) => (
        <div key={entry.label} style={{ display: "contents" }}>
          <dt style={{ ...MUTED_TEXT, margin: 0, fontSize: "var(--dayvise-font-size-sm)" }}>{entry.label}</dt>
          <dd style={{ margin: 0, fontSize: "var(--dayvise-font-size-sm)", textAlign: "right" }}>{entry.value}</dd>
        </div>
      ))}
    </dl>
  );
}

export { toneSurface };
export type { UsageMeterView };
